import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User, X, Check, Search, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * UserMentionInput - Input with @ mention autocomplete
 * Used for comments and task descriptions
 */
export function UserMentionInput({ 
  value, 
  onChange, 
  placeholder = "Tapez @ pour mentionner...",
  className,
  onMentionsChange
}) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const token = localStorage.getItem('token');

  const fetchUsers = useCallback(async (query) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/team/users?search=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSuggestions(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const pos = e.target.selectionStart;
    setCursorPosition(pos);
    onChange(newValue);

    // Check for @ mention trigger
    const textBeforeCursor = newValue.slice(0, pos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (atMatch) {
      const query = atMatch[1];
      setSearchQuery(query);
      setShowSuggestions(true);
      fetchUsers(query);
    } else {
      setShowSuggestions(false);
    }
  };

  const insertMention = (user) => {
    const textBeforeCursor = value.slice(0, cursorPosition);
    const textAfterCursor = value.slice(cursorPosition);
    
    // Find the @ position
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const beforeAt = value.slice(0, atIndex);
    
    // Insert the mention
    const mention = `@${user.name || user.email} `;
    const newValue = beforeAt + mention + textAfterCursor;
    
    onChange(newValue);
    setShowSuggestions(false);
    
    // Notify parent of mentions
    if (onMentionsChange) {
      const mentions = extractMentions(newValue);
      onMentionsChange(mentions);
    }
    
    // Focus back to input
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const newPos = beforeAt.length + mention.length;
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  const extractMentions = (text) => {
    const mentions = [];
    const regex = /@(\w+(?:\s+\w+)?)/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      mentions.push(match[1]);
    }
    return mentions;
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={className}
        onKeyDown={(e) => {
          if (showSuggestions && e.key === 'Escape') {
            setShowSuggestions(false);
          }
        }}
      />
      
      {/* Suggestions dropdown */}
      {showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-auto">
          {loading ? (
            <div className="p-3 flex items-center justify-center text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Recherche...
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((user) => (
              <button
                key={user.id}
                onClick={() => insertMention(user)}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-medium">
                  {user.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user.name || user.email}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
              </button>
            ))
          ) : (
            <div className="p-3 text-sm text-gray-500 text-center">
              Aucun utilisateur trouvé
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * UserAssignSelect - Multi-select for assigning users to a task
 */
export function UserAssignSelect({ 
  selectedUsers = [], 
  onChange,
  placeholder = "Assigner à...",
  className
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const token = localStorage.getItem('token');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/tasks/team/users?search=${encodeURIComponent(search)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [search, token]);

  useEffect(() => {
    if (showDropdown) {
      fetchUsers();
    }
  }, [showDropdown, search, fetchUsers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleUser = (user) => {
    const isSelected = selectedUsers.some(u => u.id === user.id);
    if (isSelected) {
      onChange(selectedUsers.filter(u => u.id !== user.id));
    } else {
      onChange([...selectedUsers, user]);
    }
  };

  const removeUser = (userId, e) => {
    e.stopPropagation();
    onChange(selectedUsers.filter(u => u.id !== userId));
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Selected users display */}
      <button 
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        className="w-full min-h-[42px] px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-gray-800 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {selectedUsers.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {selectedUsers.map((user) => (
              <Badge 
                key={user.id} 
                variant="secondary"
                className="flex items-center gap-1 pr-1"
              >
                <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-[8px] font-medium">
                  {user.initials}
                </div>
                <span className="text-xs">{user.name || user.email}</span>
                <span
                  onClick={(e) => removeUser(user.id, e)}
                  className="ml-1 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-full p-0.5 cursor-pointer"
                >
                  <X className="h-3 w-3" />
                </span>
              </Badge>
            ))}
            <span className="text-blue-500 text-xs flex items-center">+ Ajouter</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-500">
            <User className="h-4 w-4" />
            <span className="text-sm">{placeholder}</span>
            <span className="text-blue-500 text-xs ml-auto">Cliquer pour sélectionner</span>
          </div>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute z-[100] w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl">
          {/* Search */}
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un utilisateur..."
                className="pl-8 h-9 text-sm"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Users list */}
          <div className="max-h-52 overflow-y-auto">
            {loading ? (
              <div className="p-4 flex items-center justify-center text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Chargement...
              </div>
            ) : users.length > 0 ? (
              <div className="py-1">
                {users.map((user) => {
                  const isSelected = selectedUsers.some(u => u.id === user.id);
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleUser(user);
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left",
                        isSelected && "bg-blue-50 dark:bg-blue-900/30"
                      )}
                    >
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                        {user.initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {user.name || user.email}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                      {isSelected ? (
                        <Check className="h-5 w-5 text-blue-500" />
                      ) : (
                        <span className="text-xs text-gray-400">Cliquer pour assigner</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-sm text-gray-500 text-center">
                Aucun utilisateur trouvé
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-lg">
            <button
              type="button"
              onClick={() => setShowDropdown(false)}
              className="w-full py-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * UserAvatarGroup - Display a group of user avatars
 */
export function UserAvatarGroup({ users = [], max = 3, size = 'sm' }) {
  const displayUsers = users.slice(0, max);
  const remaining = users.length - max;
  
  const sizeClasses = {
    xs: 'w-5 h-5 text-[8px]',
    sm: 'w-6 h-6 text-[10px]',
    md: 'w-8 h-8 text-xs',
    lg: 'w-10 h-10 text-sm'
  };

  return (
    <div className="flex -space-x-2">
      {displayUsers.map((user, idx) => (
        <div
          key={user.id || idx}
          className={cn(
            "rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-medium border-2 border-white dark:border-gray-800",
            sizeClasses[size]
          )}
          title={user.name || user.email}
        >
          {user.initials || (user.name || user.email || 'U').charAt(0).toUpperCase()}
        </div>
      ))}
      {remaining > 0 && (
        <div
          className={cn(
            "rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-700 dark:text-gray-200 font-medium border-2 border-white dark:border-gray-800",
            sizeClasses[size]
          )}
        >
          +{remaining}
        </div>
      )}
    </div>
  );
}

export default { UserMentionInput, UserAssignSelect, UserAvatarGroup };
